import { fieldClasses } from '../ui/control.js'
import { useState } from 'react'

/**
 * kind: numeric — a number, honouring min, max, step and unit from config.
 *
 * An out-of-range entry is never silently clamped and never silently discarded:
 * it stays on screen with a plain note, and no value is emitted until it is
 * valid. Clamping would record a number the participant did not give.
 */
export function NumericEntry({ item, format, answer, onChange, disabled, name }) {
  const min = typeof format.min === 'number' ? format.min : null
  const max = typeof format.max === 'number' ? format.max : null
  const step = typeof format.step === 'number' ? format.step : 1
  const wholeNumbers = step === 1

  // draft holds text that is not yet a valid value, so a typo does not wipe a
  // previously saved answer and does not vanish on blur.
  const [draft, setDraft] = useState(null)
  const stored = answer?.value ?? ''
  const shown = draft === null ? String(stored) : draft

  const problem = validate(draft, { min, max, wholeNumbers })

  function handle(text) {
    if (text === '') {
      setDraft(null)
      onChange({ ...answer, value: null })
      return
    }
    setDraft(text)
    if (!validate(text, { min, max, wholeNumbers })) {
      setDraft(null)
      onChange({ ...answer, value: Number(text) })
    }
  }

  const describedBy = []
  if (format.zero_means) describedBy.push(`${name}-zero`)
  if (problem) describedBy.push(`${name}-error`)

  return (
    <div>
      <div className="flex items-center gap-3">
        <input
          id={name}
          name={name}
          type="number"
          inputMode="numeric"
          // A phone keypad, not a full keyboard.
          pattern={wholeNumbers ? '[0-9]*' : undefined}
          value={shown}
          min={min ?? undefined}
          max={max ?? undefined}
          step={step}
          disabled={disabled}
          onChange={(e) => handle(e.target.value)}
          aria-invalid={problem ? true : undefined}
          aria-describedby={describedBy.join(' ') || undefined}
          className={'w-[8em] text-base ' + fieldClasses({ invalid: Boolean(problem) })}
        />
        {format.unit ? <span className="text-base text-[var(--ink-700)]">{format.unit}</span> : null}
      </div>

      {/* Both hints below are config text, not copy written into this component. */}
      {format.zero_means ? (
        <p id={`${name}-zero`} className="mt-2 text-sm text-[var(--ink-700)]">
          0 means {format.zero_means}.
        </p>
      ) : null}
      {problem ? (
        <p id={`${name}-error`} role="alert" className="mt-2 text-sm text-[var(--critical)]">
          {problem}
        </p>
      ) : null}
    </div>
  )
}

function validate(text, { min, max, wholeNumbers }) {
  if (text === null || text === '') return null
  const n = Number(text)
  if (!Number.isFinite(n)) return 'Enter a number.'
  if (wholeNumbers && !Number.isInteger(n)) return 'Enter a whole number.'
  if (min !== null && max !== null && (n < min || n > max)) {
    return `Enter a number between ${min} and ${max}.`
  }
  if (min !== null && n < min) return `Enter ${min} or more.`
  if (max !== null && n > max) return `Enter ${max} or less.`
  return null
}
